import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';

/**
 * AssumptionsSheet Component
 * 
 * Implements controlled two-way sync between frontend and Google Sheet
 * 
 * READ-ONLY FIELDS (fetch only, never write):
 * - B6: S&M % of Sales (uses benchmark)
 * - B11: Inventory Days (on COGS)
 * - E29:E31: COGS % of Sales for Products A, B, Service C
 * 
 * EDITABLE FIELDS:
 * - Dropdowns: B3 (Chosen Industry), B4 (Business Model), F23:F24 (Loan Modes)
 * - Numeric: B5, B7, B9-B10, B13:D15, B19:D19, B23:E24, B29:D31
 * 
 * CRITICAL: Only updates specified cells, never creates new rows/columns
 */
const AssumptionsSheet = ({ hasAccess, onRequestAccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    // Core Assumptions (A1:B11)
    chosenIndustry: 'SAAS',        // B3 - Editable dropdown
    businessModel: 'Goods',         // B4 - Editable dropdown
    taxRate: '',                    // B5 - Editable numeric
    smPercent: '',                  // B6 - READ-ONLY (fetched from sheet)
    varOpexPercent: '',             // B7 - Editable numeric
    arDays: '',                     // B9 - Editable numeric
    apDays: '',                     // B10 - Editable numeric
    inventoryDays: '',              // B11 - READ-ONLY (fetched from sheet)
    
    // Payroll Statutory Adders (Row 15: A15:D15)
    employerPF: '',                 // A15 - Editable numeric with % handling
    esic: '',                       // B15 - Editable numeric with % handling
    gratuity: '',                   // C15 - Editable numeric with % handling
    avgIncrement: '',               // D15 - Editable numeric with % handling
    
    // CapEx (A17:D19)
    expansionCapex: '',             // A19 - Editable numeric
    expansionLife: '',              // B19 - Editable numeric
    maintenanceCapex: '',           // C19 - Editable numeric
    maintenanceLife: '',            // D19 - Editable numeric
    
    // Loans (A21:F25)
    loan1Amount: '',                // B23 - Editable numeric
    loan1DrawMonth: '',             // C23 - Editable numeric
    loan1Tenor: '',                 // D23 - Editable numeric
    loan1Interest: '',              // E23 - Editable numeric
    loan1Mode: 'EMI',               // F23 - Editable dropdown
    loan2Amount: '',                // B24 - Editable numeric
    loan2DrawMonth: '',             // C24 - Editable numeric
    loan2Tenor: '',                 // D24 - Editable numeric
    loan2Interest: '',              // E24 - Editable numeric
    loan2Mode: 'Straight-Line',     // F24 - Editable dropdown
    
    // Products/Services (A27:E31)
    productAVolume: '',             // B29 - Editable numeric
    productAGrowth: '',             // C29 - Editable numeric
    productAPrice: '',              // D29 - Editable numeric
    productACogs: '',               // E29 - READ-ONLY (fetched from sheet)
    productBVolume: '',             // B30 - Editable numeric
    productBGrowth: '',             // C30 - Editable numeric
    productBPrice: '',              // D30 - Editable numeric
    productBCogs: '',               // E30 - READ-ONLY (fetched from sheet)
    serviceCVolume: '',             // B31 - Editable numeric
    serviceCGrowth: '',             // C31 - Editable numeric
    serviceCPrice: '',              // D31 - Editable numeric
    serviceCCogs: ''                // E31 - READ-ONLY (fetched from sheet)
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  // Helper function: Remove "%" symbol from fetched value
  const removePercentage = (value) => {
    if (typeof value === 'string') {
      return value.replace('%', '').trim();
    }
    return value;
  };

  // Helper function: Add "%" symbol when saving to sheet
  const addPercentage = (value) => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    // If already has %, return as-is
    if (typeof value === 'string' && value.includes('%')) {
      return value;
    }
    return `${value}%`;
  };

  // Fields that require percentage handling
  const percentageFields = [
    'taxRate',          // B5
    'varOpexPercent',   // B7
    'employerPF',       // A15
    'esic',             // B15
    'gratuity',         // C15
    'avgIncrement',     // D15
    'loan1Interest',    // E23
    'loan2Interest'     // E24
  ];

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/sheets/Assumptions`);
      const sheetData = response.data.data;
      const parsed = { ...data };
      
      console.log('Fetched sheet data:', sheetData);
      console.log('Row 15 (index 14):', sheetData[14]);

      for (let i = 0; i < sheetData.length; i++) {
        const row = sheetData[i];
        const label = row[0];
        
        // Core Assumptions
        if (label === 'Chosen Industry') parsed.chosenIndustry = row[1] || 'SAAS';
        if (label === 'Business Model') parsed.businessModel = row[1] || 'Goods';
        if (label === 'Tax Rate') parsed.taxRate = removePercentage(row[1]) || '';
        if (label === 'S&M % of Sales (override or uses benchmark)') parsed.smPercent = removePercentage(row[1]) || '';
        if (label === 'Variable Opex % of Sales') parsed.varOpexPercent = removePercentage(row[1]) || '';
        if (label === 'AR Days') parsed.arDays = row[1] || '';
        if (label === 'AP Days') parsed.apDays = row[1] || '';
        if (label === 'Inventory Days (on COGS)') parsed.inventoryDays = row[1] || '';
        
        // Payroll Statutory Adders - Direct read from Row 15 (A15:D15)
if (i === 14 && row.length >= 4) {
  parsed.employerPF = removePercentage(row[0]) || '';
  parsed.esic = removePercentage(row[1]) || '';
  parsed.gratuity = removePercentage(row[2]) || '';
  parsed.avgIncrement = removePercentage(row[3]) || '';
  console.log('Payroll data from Row 15:', { 
    employerPF: parsed.employerPF, 
    esic: parsed.esic, 
    gratuity: parsed.gratuity, 
    avgIncrement: parsed.avgIncrement 
  });
}
        
        // CapEx - Direct read from Row 19 (A19:D19)
// Row 19 is at index 18 (0-based indexing)
if (i === 18 && row.length >= 4) {
  parsed.expansionCapex = row[0] || '';      // A19 - Expansion CapEx (Year)
  parsed.expansionLife = row[1] || '';       // B19 - Life (yrs)
  parsed.maintenanceCapex = row[2] || '';    // C19 - Maintenance CapEx (Year)
  parsed.maintenanceLife = row[3] || '';     // D19 - Life (yrs)
  console.log('CapEx data from Row 19 (A19:D19):', { 
    expansionCapex: parsed.expansionCapex, 
    expansionLife: parsed.expansionLife, 
    maintenanceCapex: parsed.maintenanceCapex, 
    maintenanceLife: parsed.maintenanceLife 
  });
}
        
        // Loans
        if (label === 'Loan 1') {
          parsed.loan1Amount = row[1] || '';
          parsed.loan1DrawMonth = row[2] || '';
          parsed.loan1Tenor = row[3] || '';
          parsed.loan1Interest = removePercentage(row[4]) || '';
          parsed.loan1Mode = row[5] || 'EMI';
        }
        
        if (label === 'Loan 2') {
          parsed.loan2Amount = row[1] || '';
          parsed.loan2DrawMonth = row[2] || '';
          parsed.loan2Tenor = row[3] || '';
          parsed.loan2Interest = removePercentage(row[4]) || '';
          parsed.loan2Mode = row[5] || 'Straight-Line';
        }
        
        // Products/Services - Direct read from Rows 29-31 (B29:E31)
        // Product A - Row 29 (index 28)
        if (i === 28) {
          parsed.productAVolume = row[1] || '';     // B29
          parsed.productAGrowth = row[2] || '';     // C29
          parsed.productAPrice = row[3] || '';      // D29
          parsed.productACogs = row[4] ? removePercentage(row[4]) : '';  // E29 - READ-ONLY
          console.log('Product A data from Row 29 (full row):', row);
          console.log('Product A parsed:', {
            volume: parsed.productAVolume, 
            growth: parsed.productAGrowth, 
            price: parsed.productAPrice, 
            cogs: parsed.productACogs,
            rawCogs: row[4]
          });
        }
        
        // Product B - Row 30 (index 29)
        if (i === 29) {
          parsed.productBVolume = row[1] || '';     // B30
          parsed.productBGrowth = row[2] || '';     // C30
          parsed.productBPrice = row[3] || '';      // D30
          parsed.productBCogs = row[4] ? removePercentage(row[4]) : '';  // E30 - READ-ONLY
          console.log('Product B data from Row 30 (full row):', row);
          console.log('Product B parsed:', { 
            volume: parsed.productBVolume, 
            growth: parsed.productBGrowth, 
            price: parsed.productBPrice, 
            cogs: parsed.productBCogs,
            rawCogs: row[4]
          });
        }
        
        // Service C - Row 31 (index 30)
         if (i === 30) {
          parsed.serviceCVolume = row[1] || '';     // B31
          parsed.serviceCGrowth = row[2] || '';     // C31
          parsed.serviceCPrice = row[3] || '';      // D31
          parsed.serviceCCogs = row[4] ? removePercentage(row[4]) : '';  // E31 - READ-ONLY
          console.log('Service C data from Row 31 (full row):', row);
          console.log('Service C parsed:', { 
            volume: parsed.serviceCVolume, 
            growth: parsed.serviceCGrowth, 
            price: parsed.serviceCPrice, 
            cogs: parsed.serviceCCogs,
            rawCogs: row[4]
          });
        }
      }
      
      setData(parsed);
    } catch (error) {
      console.error('Failed to load assumptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const autoSave = async (field, value) => {
    if (!hasAccess) {
      onRequestAccess(); // Show payment dialog
      return;
    }
    setSaving(true);
    try {
      // EXACT cell mappings per specification
      // Only these cells are editable from frontend
      const cellMappings = {
        // Core Assumptions - Editable only
        chosenIndustry: 'B3',      // Dropdown
        businessModel: 'B4',       // Dropdown
        taxRate: 'B5',             // Numeric
        // smPercent: 'B6',        // READ-ONLY - NEVER WRITTEN
        varOpexPercent: 'B7',      // Numeric
        arDays: 'B9',              // Numeric
        apDays: 'B10',             // Numeric
        // inventoryDays: 'B11',   // READ-ONLY - NEVER WRITTEN
        
        // Payroll Statutory Adders (Row 15)
        employerPF: 'A15',         // Numeric with % handling
        esic: 'B15',               // Numeric with % handling
        gratuity: 'C15',           // Numeric with % handling
        avgIncrement: 'D15',       // Numeric with % handling
        
        // CapEx (Row 19: A19:D19)
        expansionCapex: 'A19',     // Numeric
        expansionLife: 'B19',      // Numeric
        maintenanceCapex: 'C19',   // Numeric
        maintenanceLife: 'D19',    // Numeric
        
        // Loans (Rows 23-24)
        loan1Amount: 'B23',        // Numeric
        loan1DrawMonth: 'C23',     // Numeric
        loan1Tenor: 'D23',         // Numeric
        loan1Interest: 'E23',      // Numeric
        loan1Mode: 'F23',          // Dropdown
        loan2Amount: 'B24',        // Numeric
        loan2DrawMonth: 'C24',     // Numeric
        loan2Tenor: 'D24',         // Numeric
        loan2Interest: 'E24',      // Numeric
        loan2Mode: 'F24',          // Dropdown
        
        // Products/Services (Rows 29-31) - Only columns B, C, D editable
        productAVolume: 'B29',     // Numeric
        productAGrowth: 'C29',     // Numeric
        productAPrice: 'D29',      // Numeric
        // productACogs: 'E29',    // READ-ONLY - NEVER WRITTEN
        productBVolume: 'B30',     // Numeric
        productBGrowth: 'C30',     // Numeric
        productBPrice: 'D30',      // Numeric
        // productBCogs: 'E30',    // READ-ONLY - NEVER WRITTEN
        serviceCVolume: 'B31',     // Numeric
        serviceCGrowth: 'C31',     // Numeric
        serviceCPrice: 'D31',      // Numeric
        // serviceCCogs: 'E31'     // READ-ONLY - NEVER WRITTEN
      };

      if (field && cellMappings[field]) {
        // Apply percentage formatting for specific fields
        const valueToSave = percentageFields.includes(field) 
          ? addPercentage(value) 
          : value;
        
        await axios.post(`${API}/sheets/update`, {
          sheet_name: 'Assumptions',
          range: cellMappings[field],
          values: [[valueToSave]]
        });
        toast.success('Saved successfully');
        setTimeout(fetchData, 1000);
      } else if (field && !cellMappings[field]) {
        console.warn(`Field "${field}" is read-only and cannot be saved`);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Auto-save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    if (!hasAccess) {
      onRequestAccess(); // trigger payment popup
      return;
    }
    setData({ ...data, [field]: value });
  };

  if (loading) {
    return <div className="p-6 text-slate-600">Loading assumptions...</div>;
  }

  return (
    <div className="space-y-6 fade-in">
      {saving && (
        <div className="fixed top-20 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Saving...</span>
        </div>
      )}

      {/* Core Assumptions */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Core Assumptions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Chosen Industry</label>
            <Select value={data.chosenIndustry} onValueChange={(val) => { handleChange('chosenIndustry', val); autoSave('chosenIndustry', val); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FMCG">FMCG</SelectItem>
                <SelectItem value="IT Services">IT Services</SelectItem>
                <SelectItem value="Auto">Auto</SelectItem>
                <SelectItem value="Logistics">Logistics</SelectItem>
                <SelectItem value="Education">Education</SelectItem>
                <SelectItem value="SAAS">SAAS</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Business Model</label>
            <Select value={data.businessModel} onValueChange={(val) => { handleChange('businessModel', val); autoSave('businessModel', val); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Goods">Goods</SelectItem>
                <SelectItem value="Service">Service</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tax Rate</label>
            <Input type="number" step="0.01" value={data.taxRate} onChange={(e) => handleChange('taxRate', e.target.value)} onBlur={(e) => autoSave('taxRate', e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              S&M % of Sales <span className="text-xs text-slate-500">(Read-only, uses benchmark)</span>
            </label>
            <Input 
              type="number" 
              step="0.01" 
              value={data.smPercent} 
              disabled 
              readOnly
              className="bg-slate-100 cursor-not-allowed"
              title="This value is calculated from benchmarks and cannot be edited"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Variable Opex % of Sales</label>
            <Input type="number" step="0.01" value={data.varOpexPercent} onChange={(e) => handleChange('varOpexPercent', e.target.value)} onBlur={(e) => autoSave('varOpexPercent', e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">AR Days</label>
            <Input type="number" value={data.arDays} onChange={(e) => handleChange('arDays', e.target.value)} onBlur={(e) => autoSave('arDays', e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">AP Days</label>
            <Input type="number" value={data.apDays} onChange={(e) => handleChange('apDays', e.target.value)} onBlur={(e) => autoSave('apDays', e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Inventory Days (on COGS) <span className="text-xs text-slate-500">(Read-only)</span>
            </label>
            <Input 
              type="number" 
              value={data.inventoryDays} 
              disabled 
              readOnly
              className="bg-slate-100 cursor-not-allowed"
              title="This value is read-only and fetched from the Google Sheet"
            />
          </div>
        </div>
      </div>

      {/* Payroll Statutory Adders */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-800 mb-4">Payroll Statutory Adders</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Employer PF %</label>
            <Input type="number" step="0.01" value={data.employerPF} onChange={(e) => handleChange('employerPF', e.target.value)} onBlur={(e) => autoSave('employerPF', e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">ESIC %</label>
            <Input type="number" step="0.01" value={data.esic} onChange={(e) => handleChange('esic', e.target.value)} onBlur={(e) => autoSave('esic', e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Gratuity %</label>
            <Input type="number" step="0.01" value={data.gratuity} onChange={(e) => handleChange('gratuity', e.target.value)} onBlur={(e) => autoSave('gratuity', e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Avg Increment % (annual)</label>
            <Input type="number" step="0.01" value={data.avgIncrement} onChange={(e) => handleChange('avgIncrement', e.target.value)} onBlur={(e) => autoSave('avgIncrement', e.target.value)} className="h-9" />
          </div>
        </div>
      </div>

      {/* CapEx */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-800 mb-4">CapEx</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Expansion CapEx (Year)</label>
            <Input type="number" value={data.expansionCapex} onChange={(e) => handleChange('expansionCapex', e.target.value)} onBlur={(e) => autoSave('expansionCapex', e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Life (yrs)</label>
            <Input type="number" value={data.expansionLife} onChange={(e) => handleChange('expansionLife', e.target.value)} onBlur={(e) => autoSave('expansionLife', e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Maintenance CapEx (Year)</label>
            <Input type="number" value={data.maintenanceCapex} onChange={(e) => handleChange('maintenanceCapex', e.target.value)} onBlur={(e) => autoSave('maintenanceCapex', e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Life (yrs)</label>
            <Input type="number" value={data.maintenanceLife} onChange={(e) => handleChange('maintenanceLife', e.target.value)} onBlur={(e) => autoSave('maintenanceLife', e.target.value)} className="h-9" />
          </div>
        </div>
      </div>

      {/* Loans */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-800 mb-4">Loans</h3>
        
        <div className="space-y-4">
          {/* Loan 1 */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Loan</label>
              <Input value="Loan 1" disabled className="bg-white h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Amount</label>
              <Input type="number" value={data.loan1Amount} onChange={(e) => handleChange('loan1Amount', e.target.value)} onBlur={(e) => autoSave('loan1Amount', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Draw Month (1-12)</label>
              <Input type="number" min="1" max="12" value={data.loan1DrawMonth} onChange={(e) => handleChange('loan1DrawMonth', e.target.value)} onBlur={(e) => autoSave('loan1DrawMonth', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Tenor (months)</label>
              <Input type="number" value={data.loan1Tenor} onChange={(e) => handleChange('loan1Tenor', e.target.value)} onBlur={(e) => autoSave('loan1Tenor', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Interest p.a.</label>
              <Input type="number" step="0.01" value={data.loan1Interest} onChange={(e) => handleChange('loan1Interest', e.target.value)} onBlur={(e) => autoSave('loan1Interest', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Mode</label>
              <Select value={data.loan1Mode} onValueChange={(val) => { handleChange('loan1Mode', val); autoSave('loan1Mode', val); }}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMI">EMI</SelectItem>
                  <SelectItem value="Straight-Line">Straight-Line</SelectItem>
                  <SelectItem value="Bullet">Bullet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Loan 2 */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Loan</label>
              <Input value="Loan 2" disabled className="bg-white h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Amount</label>
              <Input type="number" value={data.loan2Amount} onChange={(e) => handleChange('loan2Amount', e.target.value)} onBlur={(e) => autoSave('loan2Amount', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Draw Month (1-12)</label>
              <Input type="number" min="1" max="12" value={data.loan2DrawMonth} onChange={(e) => handleChange('loan2DrawMonth', e.target.value)} onBlur={(e) => autoSave('loan2DrawMonth', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Tenor (months)</label>
              <Input type="number" value={data.loan2Tenor} onChange={(e) => handleChange('loan2Tenor', e.target.value)} onBlur={(e) => autoSave('loan2Tenor', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Interest p.a.</label>
              <Input type="number" step="0.01" value={data.loan2Interest} onChange={(e) => handleChange('loan2Interest', e.target.value)} onBlur={(e) => autoSave('loan2Interest', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Mode</label>
              <Select value={data.loan2Mode} onValueChange={(val) => { handleChange('loan2Mode', val); autoSave('loan2Mode', val); }}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMI">EMI</SelectItem>
                  <SelectItem value="Straight-Line">Straight-Line</SelectItem>
                  <SelectItem value="Bullet">Bullet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Products / Services */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-800 mb-4">Products / Services (up to 3)</h3>
        
        <div className="space-y-4">
          {/* Product A */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Product</label>
              <Input value="Product A" disabled className="bg-white h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Start Volume</label>
              <Input type="number" value={data.productAVolume} onChange={(e) => handleChange('productAVolume', e.target.value)} onBlur={(e) => autoSave('productAVolume', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Monthly Growth %</label>
              <Input type="number" step="0.01" value={data.productAGrowth} onChange={(e) => handleChange('productAGrowth', e.target.value)} onBlur={(e) => autoSave('productAGrowth', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Price / Unit</label>
              <Input type="number" value={data.productAPrice} onChange={(e) => handleChange('productAPrice', e.target.value)} onBlur={(e) => autoSave('productAPrice', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                COGS % of Sales <span className="text-[10px] text-slate-500">(Read-only)</span>
              </label>
              <Input 
                type="number" 
                step="0.01" 
                value={data.productACogs} 
                disabled 
                readOnly
                className="bg-slate-100 cursor-not-allowed h-9"
                title="This value is read-only and fetched from the Google Sheet"
              />
            </div>
          </div>

          {/* Product B */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Product</label>
              <Input value="Product B" disabled className="bg-white h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Start Volume</label>
              <Input type="number" value={data.productBVolume} onChange={(e) => handleChange('productBVolume', e.target.value)} onBlur={(e) => autoSave('productBVolume', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Monthly Growth %</label>
              <Input type="number" step="0.01" value={data.productBGrowth} onChange={(e) => handleChange('productBGrowth', e.target.value)} onBlur={(e) => autoSave('productBGrowth', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Price / Unit</label>
              <Input type="number" value={data.productBPrice} onChange={(e) => handleChange('productBPrice', e.target.value)} onBlur={(e) => autoSave('productBPrice', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                COGS % of Sales <span className="text-[10px] text-slate-500">(Read-only)</span>
              </label>
              <Input 
                type="number" 
                step="0.01" 
                value={data.productBCogs} 
                disabled 
                readOnly
                className="bg-slate-100 cursor-not-allowed h-9"
                title="This value is read-only and fetched from the Google Sheet"
              />
            </div>
          </div>

          {/* Service C */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Product</label>
              <Input value="Service C" disabled className="bg-white h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Start Volume</label>
              <Input type="number" value={data.serviceCVolume} onChange={(e) => handleChange('serviceCVolume', e.target.value)} onBlur={(e) => autoSave('serviceCVolume', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Monthly Growth %</label>
              <Input type="number" step="0.01" value={data.serviceCGrowth} onChange={(e) => handleChange('serviceCGrowth', e.target.value)} onBlur={(e) => autoSave('serviceCGrowth', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Price / Unit</label>
              <Input type="number" value={data.serviceCPrice} onChange={(e) => handleChange('serviceCPrice', e.target.value)} onBlur={(e) => autoSave('serviceCPrice', e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                COGS % of Sales <span className="text-[10px] text-slate-500">(Read-only)</span>
              </label>
              <Input 
                type="number" 
                step="0.01" 
                value={data.serviceCCogs} 
                disabled 
                readOnly
                className="bg-slate-100 cursor-not-allowed h-9"
                title="This value is read-only and fetched from the Google Sheet"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssumptionsSheet;
